/**
 * The server's last word with everything since folded in.
 *
 * <p>This is the only place the two halves meet. The screen renders what this returns, and
 * the local scoring engine scores it — so an auditor filling in questions with no signal
 * sees exactly what they would see with one, including the running total and the list of
 * what is still owed.</p>
 *
 * <p><b>It is a fold, not a merge.</b> The operations are replayed in the order they were
 * made, over a state the server produced, which is the same order and the same starting
 * point the server will use when they reach it. Nothing here has to decide which of two
 * versions wins, because there is only ever one writer of a draft audit: the auditor
 * holding the phone.</p>
 */
import type { AuditAnswer, AuditDetail } from '../api/audits';
import type { Pending } from './store';

/** A photograph that is on the device and not yet on the server. */
export interface LocalPhoto {
  /** The outbox row, so the screen can take it back out of the queue. */
  pendingId: number;
  questionId: string;
  url: string;
}

export interface FoldedAudit {
  detail: AuditDetail;
  /** Object URLs for photographs still in the queue. Revoked by the caller. */
  localPhotos: LocalPhoto[];
  /** Whether a submission is waiting to go up. */
  submitting: boolean;
}

const blank = (questionId: string): AuditAnswer => ({
  questionId,
  yesNo: null,
  scaleValue: null,
  selectedOptionIds: [],
  text: null,
  markedNotApplicable: false,
  structurallyNotApplicable: false,
  comment: null,
  answeredAt: null,
  photos: [],
});

/**
 * Clears every shape an answer can take.
 *
 * <p>Mirrors AuditAnswer.Reset on the server, and for the same reason: an auditor changing
 * their mind must not leave a record saying two things at once. A local copy that kept the
 * old scale value would score differently from the server the moment it synced.</p>
 */
function reset(answer: AuditAnswer): AuditAnswer {
  return {
    ...answer,
    yesNo: null,
    scaleValue: null,
    selectedOptionIds: [],
    text: null,
  };
}

/**
 * @param objectUrl Where a queued file's address comes from. The screen hands in one that
 *   returns the same address for the same queue entry every time: a fresh one per fold
 *   meant the old one was revoked under whatever was showing it, so an open photograph or a
 *   playing video lost its file the moment anything else on the audit changed.
 */
export function fold(
  detail: AuditDetail,
  pending: Pending[],
  objectUrl: (pendingId: number, blob: Blob) => string = (_, blob) => URL.createObjectURL(blob),
): FoldedAudit {
  const answers = new Map(detail.answers.map((a) => [a.questionId, { ...a }]));
  const localPhotos: LocalPhoto[] = [];
  let notes = detail.notes;
  let signature = detail.signature;
  let submitting = false;

  for (const item of pending) {
    switch (item.op.kind) {
      case 'answer': {
        const { questionId, body } = item.op;
        let answer = answers.get(questionId) ?? blank(questionId);

        if (body.clear === true) {
          answer = { ...reset(answer), markedNotApplicable: false, answeredAt: null };
        } else if (body.notApplicable === true) {
          answer = { ...reset(answer), markedNotApplicable: true };
        } else {
          if ('yesNo' in body && body.yesNo != null) {
            answer = { ...reset(answer), yesNo: body.yesNo, markedNotApplicable: false };
          } else if ('scaleValue' in body && body.scaleValue != null) {
            answer = {
              ...reset(answer),
              scaleValue: body.scaleValue,
              markedNotApplicable: false,
            };
          } else if ('selectedOptionIds' in body && body.selectedOptionIds != null) {
            answer = {
              ...reset(answer),
              selectedOptionIds: body.selectedOptionIds,
              markedNotApplicable: false,
            };
          } else if ('text' in body && body.text != null) {
            answer = { ...reset(answer), text: body.text, markedNotApplicable: false };
          }
        }

        // The comment rides along with any of the above, and may also arrive alone.
        if ('comment' in body) answer = { ...answer, comment: body.comment || null };

        answers.set(questionId, answer);
        break;
      }

      case 'notes':
        notes = item.op.notes;
        break;

      case 'photo': {
        const answer = answers.get(item.op.questionId) ?? blank(item.op.questionId);
        const url = objectUrl(item.id, item.op.blob);

        localPhotos.push({ pendingId: item.id, questionId: item.op.questionId, url });

        answers.set(item.op.questionId, {
          ...answer,
          photos: [
            ...answer.photos,
            {
              // The outbox row's id, so the screen can address it before the server has
              // given it one. Prefixed so it cannot collide with a real identifier.
              id: `pending:${item.id}`,
              contentType: item.op.type,
              sizeInBytes: item.op.blob.size,
              capturedAt: new Date(item.at).toISOString(),
              url,
            },
          ],
        });
        break;
      }

      case 'removePhoto': {
        const photoId = item.op.photoId;

        for (const [questionId, answer] of answers) {
          if (answer.photos.some((p) => p.id === photoId)) {
            answers.set(questionId, {
              ...answer,
              photos: answer.photos.filter((p) => p.id !== photoId),
            });
          }
        }
        break;
      }

      case 'sign': {
        const url = objectUrl(item.id, item.op.blob);

        // Queued alongside the photographs so the object URL is released the same way.
        // The empty question id says it belongs to the visit rather than to a question.
        localPhotos.push({ pendingId: item.id, questionId: '', url });

        signature = {
          signedByName: item.op.name,
          signedAt: new Date(item.at).toISOString(),
          url,
        };
        break;
      }

      case 'removeSignature':
        signature = null;
        break;

      case 'submit':
        submitting = true;
        break;
    }
  }

  return {
    detail: { ...detail, answers: [...answers.values()], notes, signature },
    localPhotos,
    submitting,
  };
}
